import React, { useState, useEffect } from 'react';
import { Settings, Palette, Download, Upload, Save, RefreshCw, Eye, Copy, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  COMPANY_CONFIG, 
  THEME_CONFIG, 
  ASSETS_CONFIG, 
  APP_CONFIG,
  PREDEFINED_THEMES,
  applyTheme,
  generateEnvFile,
  validateBrandingConfig
} from '../config/branding';

const BrandingConfig = ({ isOpen, onClose, currentUser }) => {
  const [config, setConfig] = useState({
    // Empresa
    companyName: COMPANY_CONFIG.name,
    companyTagline: COMPANY_CONFIG.tagline,
    companyPhone: COMPANY_CONFIG.phone,
    companyEmail: COMPANY_CONFIG.email,
    supportEmail: COMPANY_CONFIG.supportEmail,
    
    // Colores
    primaryColor: THEME_CONFIG.primary,
    accentColor: THEME_CONFIG.accent,
    backgroundColor: THEME_CONFIG.background,
    textColor: THEME_CONFIG.text,
    
    // Assets
    logoPath: ASSETS_CONFIG.logo,
    faviconPath: ASSETS_CONFIG.favicon,
    logoSmallPath: ASSETS_CONFIG.logoSmall,
    
    // App
    appTitle: APP_CONFIG.title,
    loginTitle: APP_CONFIG.loginTitle,
    dashboardTitle: APP_CONFIG.dashboardTitle,
  });

  const [activeTab, setActiveTab] = useState('company');
  const [previewMode, setPreviewMode] = useState(false);
  const [showEnvCode, setShowEnvCode] = useState(false);
  const [envCode, setEnvCode] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  // Generar vista previa de colores
  const generatePreview = (primaryColor, accentColor) => {
    return {
      primary: primaryColor,
      accent: accentColor,
      primaryHover: THEME_CONFIG.darkenColor(primaryColor, 0.1),
      primaryLight: THEME_CONFIG.lightenColor(primaryColor, 0.1),
      accentHover: THEME_CONFIG.darkenColor(accentColor, 0.1),
    };
  };

  // Aplicar tema de vista previa
  const applyPreview = () => {
    const previewTheme = generatePreview(config.primaryColor, config.accentColor);
    applyTheme(previewTheme);
    setPreviewMode(true);
  };

  // Restablecer tema original
  const resetPreview = () => {
    applyTheme();
    setPreviewMode(false);
  };

  // Aplicar tema predefinido
  const applyPredefinedTheme = (themeKey) => {
    const theme = PREDEFINED_THEMES[themeKey];
    if (theme) {
      setConfig(prev => ({
        ...prev,
        primaryColor: theme.primary,
        accentColor: theme.accent
      }));
      
      // Aplicar inmediatamente para vista previa
      applyTheme(theme);
      setPreviewMode(true);
    }
  };

  // Generar código .env
  const generateEnvCode = () => {
    const envContent = generateEnvFile({
      name: config.companyName,
      tagline: config.companyTagline,
      phone: config.companyPhone,
      email: config.companyEmail,
      supportEmail: config.supportEmail,
      primary: config.primaryColor,
      accent: config.accentColor,
      background: config.backgroundColor,
      text: config.textColor,
      logo: config.logoPath,
      favicon: config.faviconPath,
      logoSmall: config.logoSmallPath,
      title: config.appTitle,
      loginTitle: config.loginTitle,
      dashboardTitle: config.dashboardTitle,
    });
    
    setEnvCode(envContent);
    setShowEnvCode(true);
  };

  // Copiar código al portapapeles
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSaveMessage('Copiado al portapapeles');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error copiando al portapapeles:', error);
    }
  };

  // Descargar archivo .env
  const downloadEnvFile = () => {
    const blob = new Blob([envCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '.env';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setSaveMessage('Archivo .env descargado');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  // Validar configuración
  const isValidConfig = () => {
    const validation = validateBrandingConfig();
    return validation.isValid;
  };

  // Restablecer configuración por defecto
  const resetToDefaults = () => {
    setConfig({
      companyName: 'SU EMPRESA',
      companyTagline: 'Su Slogan Aquí',
      companyPhone: '+1 (555) 123-4567',
      companyEmail: 'info@suempresa.com',
      supportEmail: 'soporte@suempresa.com',
      primaryColor: '#1e40af',
      accentColor: '#f59e0b',
      backgroundColor: '#f8fafc',
      textColor: '#1f2937',
      logoPath: 'logo.svg',
      faviconPath: 'favicon.ico',
      logoSmallPath: 'logo-small.svg',
      appTitle: 'CRM Profesional',
      loginTitle: 'Iniciar Sesión',
      dashboardTitle: 'Panel de Control',
    });
    resetPreview();
  };

  if (currentUser?.role !== 'Admin') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Acceso Restringido</DialogTitle>
            <DialogDescription>
              Solo los administradores pueden acceder a la configuración de branding.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Palette className="h-5 w-5 mr-2 text-mg-green" />
            Configuración de Branding
          </DialogTitle>
          <DialogDescription>
            Personaliza la apariencia y configuración de la aplicación para tu agencia
          </DialogDescription>
        </DialogHeader>

        {saveMessage && (
          <Alert className="mb-4">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>{saveMessage}</AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="company">Empresa</TabsTrigger>
            <TabsTrigger value="colors">Colores</TabsTrigger>
            <TabsTrigger value="assets">Assets</TabsTrigger>
            <TabsTrigger value="app">Aplicación</TabsTrigger>
          </TabsList>

          {/* Pestaña Empresa */}
          <TabsContent value="company" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Información de la Empresa</CardTitle>
                <CardDescription>
                  Configura la información básica de tu agencia
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="companyName">Nombre de la Empresa</Label>
                    <Input
                      id="companyName"
                      value={config.companyName}
                      onChange={(e) => setConfig(prev => ({...prev, companyName: e.target.value}))}
                      placeholder="Su Empresa"
                    />
                  </div>
                  <div>
                    <Label htmlFor="companyTagline">Slogan</Label>
                    <Input
                      id="companyTagline"
                      value={config.companyTagline}
                      onChange={(e) => setConfig(prev => ({...prev, companyTagline: e.target.value}))}
                      placeholder="Su slogan aquí"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="companyPhone">Teléfono</Label>
                    <Input
                      id="companyPhone"
                      value={config.companyPhone}
                      onChange={(e) => setConfig(prev => ({...prev, companyPhone: e.target.value}))}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div>
                    <Label htmlFor="companyEmail">Email</Label>
                    <Input
                      id="companyEmail"
                      type="email"
                      value={config.companyEmail}
                      onChange={(e) => setConfig(prev => ({...prev, companyEmail: e.target.value}))}
                      placeholder="info@suempresa.com"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="supportEmail">Email de Soporte</Label>
                  <Input
                    id="supportEmail"
                    type="email"
                    value={config.supportEmail}
                    onChange={(e) => setConfig(prev => ({...prev, supportEmail: e.target.value}))}
                    placeholder="soporte@suempresa.com"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pestaña Colores */}
          <TabsContent value="colors" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Temas Predefinidos</CardTitle>
                <CardDescription>
                  Selecciona un tema predefinido o personaliza los colores
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  {Object.entries(PREDEFINED_THEMES).map(([key, theme]) => (
                    <div
                      key={key}
                      className="p-4 border rounded-lg cursor-pointer hover:border-gray-400 transition-colors"
                      onClick={() => applyPredefinedTheme(key)}
                    >
                      <div className="flex items-center space-x-2 mb-2">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: theme.primary }}
                        />
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: theme.accent }}
                        />
                        <span className="font-medium text-sm">{theme.name}</span>
                      </div>
                      <p className="text-xs text-gray-600">{theme.description}</p>
                    </div>
                  ))}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="primaryColor">Color Primario</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id="primaryColor"
                        type="color"
                        value={config.primaryColor}
                        onChange={(e) => setConfig(prev => ({...prev, primaryColor: e.target.value}))}
                        className="w-16 h-10 p-1 border rounded"
                      />
                      <Input
                        value={config.primaryColor}
                        onChange={(e) => setConfig(prev => ({...prev, primaryColor: e.target.value}))}
                        placeholder="#006400"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="accentColor">Color de Acento</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id="accentColor"
                        type="color"
                        value={config.accentColor}
                        onChange={(e) => setConfig(prev => ({...prev, accentColor: e.target.value}))}
                        className="w-16 h-10 p-1 border rounded"
                      />
                      <Input
                        value={config.accentColor}
                        onChange={(e) => setConfig(prev => ({...prev, accentColor: e.target.value}))}
                        placeholder="#FFD700"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2 mt-4">
                  <Button onClick={applyPreview} variant="outline">
                    <Eye className="h-4 w-4 mr-2" />
                    Vista Previa
                  </Button>
                  {previewMode && (
                    <Button onClick={resetPreview} variant="outline">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Restablecer
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pestaña Assets */}
          <TabsContent value="assets" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Logos y Assets</CardTitle>
                <CardDescription>
                  Configura las rutas de logos y otros recursos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="logoPath">Logo Principal</Label>
                  <Input
                    id="logoPath"
                    value={config.logoPath}
                    onChange={(e) => setConfig(prev => ({...prev, logoPath: e.target.value}))}
                    placeholder="logo.svg"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Ruta relativa desde la carpeta public/
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="faviconPath">Favicon</Label>
                  <Input
                    id="faviconPath"
                    value={config.faviconPath}
                    onChange={(e) => setConfig(prev => ({...prev, faviconPath: e.target.value}))}
                    placeholder="favicon.ico"
                  />
                </div>
                
                <div>
                  <Label htmlFor="logoSmallPath">Logo Pequeño</Label>
                  <Input
                    id="logoSmallPath"
                    value={config.logoSmallPath}
                    onChange={(e) => setConfig(prev => ({...prev, logoSmallPath: e.target.value}))}
                    placeholder="logo-small.svg"
                  />
                </div>
                
                <Alert>
                  <Upload className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Nota:</strong> Los archivos deben colocarse en la carpeta <code>frontend/public/</code> 
                    para estar disponibles en la aplicación.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pestaña Aplicación */}
          <TabsContent value="app" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Configuración de la Aplicación</CardTitle>
                <CardDescription>
                  Personaliza los textos y títulos de la aplicación
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="appTitle">Título de la Aplicación</Label>
                  <Input
                    id="appTitle"
                    value={config.appTitle}
                    onChange={(e) => setConfig(prev => ({...prev, appTitle: e.target.value}))}
                    placeholder="CRM Profesional"
                  />
                </div>
                
                <div>
                  <Label htmlFor="loginTitle">Título de Login</Label>
                  <Input
                    id="loginTitle"
                    value={config.loginTitle}
                    onChange={(e) => setConfig(prev => ({...prev, loginTitle: e.target.value}))}
                    placeholder="Iniciar Sesión"
                  />
                </div>
                
                <div>
                  <Label htmlFor="dashboardTitle">Título del Dashboard</Label>
                  <Input
                    id="dashboardTitle"
                    value={config.dashboardTitle}
                    onChange={(e) => setConfig(prev => ({...prev, dashboardTitle: e.target.value}))}
                    placeholder="Panel de Control"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Botones de Acción */}
        <div className="flex flex-wrap gap-2 pt-4 border-t">
          <Button onClick={generateEnvCode} className="bg-mg-green hover:bg-mg-green/90">
            <Download className="h-4 w-4 mr-2" />
            Generar .env
          </Button>
          
          <Button onClick={resetToDefaults} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Restablecer
          </Button>
          
          <div className="ml-auto">
            <Button onClick={onClose} variant="outline">
              Cerrar
            </Button>
          </div>
        </div>

        {/* Dialog para código .env */}
        <Dialog open={showEnvCode} onOpenChange={setShowEnvCode}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Archivo .env Generado</DialogTitle>
              <DialogDescription>
                Copia este contenido a tu archivo .env para aplicar los cambios
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <Textarea
                value={envCode}
                readOnly
                rows={20}
                className="font-mono text-sm"
              />
              
              <div className="flex space-x-2">
                <Button onClick={() => copyToClipboard(envCode)} variant="outline">
                  <Copy className="h-4 w-4 mr-2" />
                  Copiar
                </Button>
                
                <Button onClick={downloadEnvFile} className="bg-mg-green hover:bg-mg-green/90">
                  <Download className="h-4 w-4 mr-2" />
                  Descargar
                </Button>
              </div>
              
              <Alert>
                <Settings className="h-4 w-4" />
                <AlertDescription>
                  <strong>Importante:</strong> Después de actualizar el archivo .env, 
                  reinicia la aplicación para que los cambios surtan efecto.
                </AlertDescription>
              </Alert>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default BrandingConfig;